/**
 * Pricing Summary Hooks
 *
 * TanStack Query hooks for the PricingCard widget.
 * Fetches computed pricing data and customer groups
 * from zaviago_backend.api.pricing_summary endpoints.
 */

import { useQuery } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

// ── Types ────────────────────────────────────────────────────

export interface PricingSummary {
  base_price: number | null;
  computed_price: number | null;
  currency: string;
  price_list: string;
  customer_group: string;
  has_active_promotion: boolean;
  promotions: Promotion[];
}

export interface Promotion {
  name: string;
  title: string;
  rate_or_discount: "Discount Percentage" | "Discount Amount" | "Rate";
  discount_percentage: number | null;
  discount_amount: number | null;
  rate: number | null;
  min_qty: number | null;
  valid_from: string | null;
  valid_upto: string | null;
  status: "Active" | "Scheduled" | "Expired";
  priority: string | null;
  customer_group: string | null;
  applicable_for: string | null;
  storefront_show_on_detail: 0 | 1;
  storefront_enabled: 0 | 1;
  apply_multiple_pricing_rules: 0 | 1;
  disabled: 0 | 1;
  scope?: string;
  scope_type?: "item" | "variants" | "category" | "brand" | "store";
}

export interface CustomerGroupOption {
  name: string;
  default_price_list: string;
}

// ── Advanced Pricing Types ────────────────────────────────────

export interface ItemPriceEntry {
  name: string;
  price_list: string;
  price_list_rate: number;
  currency: string;
  valid_from: string | null;
  valid_upto: string | null;
  uom: string;
}

export interface PricingRuleEntry {
  name: string;
  title: string;
  rate_or_discount: string;
  discount_percentage: number | null;
  discount_amount: number | null;
  rate: number | null;
  min_qty: number | null;
  valid_from: string | null;
  valid_upto: string | null;
  status: string;
  priority: string | null;
  customer_group: string | null;
  applicable_for: string | null;
  currency: string | null;
}

export interface ResolutionPriorityEntry {
  rank: number;
  label: string;
  description: string;
}

export interface ItemPricingRulesResponse {
  volume_breaks: PricingRuleEntry[];
  customer_contracts: PricingRuleEntry[];
  campaigns: PricingRuleEntry[];
  resolution_priority: ResolutionPriorityEntry[];
}

// ── Pricing Table Types (multi-row endpoint) ────────────────

export interface PricingTableAppliedRule {
  name: string;
  title: string;
  rate_or_discount: string;
  value: number;
  display: string;
  valid_from: string | null;
  valid_upto: string | null;
  storefront_show_on_detail: boolean;
  selection_reason:
    | "storefront_show_on_detail"
    | "priority_winner"
    | "only_active";
}

export interface PricingTableOtherRule {
  name: string;
  title: string;
  value: number;
  display: string;
  reason_not_applied: "lower_priority" | "storefront_show_on_detail_override";
}

export interface PricingTableRow {
  price_list: string;
  is_primary: boolean;
  currency: string;
  base_price: number | null;
  customer_pays: number | null;
  savings: number;
  applied_rule: PricingTableAppliedRule | null;
  other_active_rules: PricingTableOtherRule[];
  has_item_price: boolean;
}

export interface PricingTableResponse {
  item_code: string;
  primary_price_list: string;
  customer_group: string;
  rows: PricingTableRow[];
}

// ── Query Keys ───────────────────────────────────────────────

export const pricingSummaryKeys = {
  summary: (itemCode: string, customerGroup?: string) =>
    ["pricing-summary", itemCode, customerGroup ?? ""] as const,
  customerGroups: () => ["pricing-customer-groups"] as const,
  allItemPrices: (itemCode: string) =>
    ["all-item-prices", itemCode] as const,
  itemPricingRules: (itemCode: string) =>
    ["item-pricing-rules", itemCode] as const,
  itemPricingTable: (itemCode: string, customerGroup?: string) =>
    ["itemPricingTable", itemCode, customerGroup ?? ""] as const,
};

// ── Hooks ────────────────────────────────────────────────────

/**
 * Fetch the pricing summary for an item.
 *
 * Returns base price, computed price (with best active promotion applied),
 * currency, and list of active/scheduled/expired promotions.
 *
 * The `customerGroup` param drives the "Preview as" context switcher.
 *
 * @deprecated Use useItemPricingTable instead. Kept for RunASaleModal compat.
 */
export function useItemPricingSummary(
  itemCode: string,
  customerGroup?: string
) {
  return useQuery<PricingSummary>({
    queryKey: pricingSummaryKeys.summary(itemCode, customerGroup),
    enabled: !!itemCode && itemCode !== "new",
    queryFn: () =>
      frappeMethod<PricingSummary>(
        "zaviago_backend.api.pricing_summary.get_item_pricing_summary",
        {
          item_code: itemCode,
          ...(customerGroup && customerGroup !== "All Customer Groups"
            ? { customer_group: customerGroup }
            : {}),
        }
      ),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch customer groups that have a default_price_list assigned.
 *
 * Used by the "Preview as" dropdown in PricingCard.
 * Includes "All Customer Groups" as the first option.
 */
export function usePricingCustomerGroups() {
  return useQuery<CustomerGroupOption[]>({
    queryKey: pricingSummaryKeys.customerGroups(),
    queryFn: () =>
      frappeMethod<CustomerGroupOption[]>(
        "zaviago_backend.api.pricing_summary.get_pricing_customer_groups"
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes — this data rarely changes
  });
}

/**
 * Fetch all Item Prices across all selling price lists.
 * Lazy-loaded — only fetches when `enabled` is true (panel expanded).
 */
export function useAllItemPrices(itemCode: string, enabled: boolean) {
  return useQuery<ItemPriceEntry[]>({
    queryKey: pricingSummaryKeys.allItemPrices(itemCode),
    enabled: enabled && !!itemCode && itemCode !== "new",
    queryFn: () =>
      frappeMethod<ItemPriceEntry[]>(
        "zaviago_backend.api.pricing_summary.get_all_item_prices",
        { item_code: itemCode }
      ),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch all Pricing Rules for an item, categorized by type.
 * Lazy-loaded — only fetches when `enabled` is true (panel expanded).
 */
export function useItemPricingRules(itemCode: string, enabled: boolean) {
  return useQuery<ItemPricingRulesResponse>({
    queryKey: pricingSummaryKeys.itemPricingRules(itemCode),
    enabled: enabled && !!itemCode && itemCode !== "new",
    queryFn: () =>
      frappeMethod<ItemPricingRulesResponse>(
        "zaviago_backend.api.pricing_summary.get_item_pricing_rules",
        { item_code: itemCode }
      ),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

// ── Pricing Table Hook (Plan 170) ───────────────────────────

/**
 * Fetch the multi-row pricing table for an item.
 *
 * Returns one row per Price List affecting this item. Primary (storefront)
 * row is always first. Each row includes base price, customer-pays,
 * the winning applied rule, and any other active rules.
 *
 * The `customerGroup` param drives the "Preview as" context switcher.
 */
export function useItemPricingTable(
  itemCode: string,
  customerGroup?: string
) {
  return useQuery<PricingTableResponse>({
    queryKey: pricingSummaryKeys.itemPricingTable(itemCode, customerGroup),
    enabled: !!itemCode && itemCode !== "new",
    queryFn: () =>
      frappeMethod<PricingTableResponse>(
        "zaviago_backend.api.pricing_table.get_item_pricing_table",
        {
          item_code: itemCode,
          ...(customerGroup && customerGroup !== "All Customer Groups"
            ? { customer_group: customerGroup }
            : {}),
        }
      ),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}
